import { Button, Grid, Paper, Skeleton, Stack, Table, TableBody, TableContainer, TableHead, List, ListSubheader } from '@mui/material';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormHeaderPaper, FormTableCell, FormTableRow, FormTitleTypography, DataEntryTextField, SectorTitleTypography, FormDescriptionTypography } from '../../../components/CustomStyles/StyledComponents';
import { getAllocateElectricityFuelCont, getElectricityFuelContFormData, getElectricityFuelContMetaData, 
    resetResponseState, saveElectricityFuelContData, 
    setElectricityFuelContFormData, setLoading, setWholeFarmDataCont, unsetLoading,
    fullResetElectricityFuelCont } from '../../../store/appAction';
import toastr from "toastr";
import { toastrCustomOptions, toastrCustomOptionsLonger } from "../../../constants/toastrOptions";
import _ from "lodash";
import { isEqual } from "lodash";
import { addCommas, removeCommas } from '../../../helpers/stringFormatHelper';
import {produce} from 'immer';
import DataEntryButtonStack from '../../../components/Buttons/DataEntryButtonStack';
import SelectedFarm from '../../../components/SelectedFarm/SelectedFarm';
import SelectedReport from '../../../components/SelectedReport/SelectedReport';
import store from '../../../store';

const CELL_WIDTH = {
    RowTitle: "20%",
    Electricity: "16%",
    RedDieselContractorsUse: "16%",
    RedDieselGrainDrying: "16%",
    WhiteDiesel: "16%",
    Petrol: "16%",
    MainsGas: "16%",
    Kerosene: "16%",
    LPG: "16%",
    Coal: "16%",
    GeneralCell: "16%"
};

const DataEntryTable = ({ sector, initialFormData }) => {
    toastr.options = toastrCustomOptions;
    const dispatch = useDispatch();
    const [sectorDataInt, setSectorDataInt] = useState(undefined);
    const [sectorDataExt, setSectorDataExt] = useState(undefined);

    useEffect(() => {
        setSectorDataInt(initialFormData);
        setSectorDataExt(initialFormData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFormData]);

    useEffect(() => {
        if (sectorDataExt && !_.isEmpty(sectorDataExt)) {
            dispatch(setElectricityFuelContFormData({
                sectorId: sector.id,
                formData: sectorDataExt
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectorDataExt]);

    const handleInputChange = useCallback((e, rowId, colId, rule) => {
        let value = removeCommas(e.target.value);
        if (e.target.inputMode === "numeric") {
            let numericRegex = /^-?[0-9]*(\.[0-9]{0,2})?$/;
            if (numericRegex.test(value)) {
                if (rule.max < parseFloat(value || 0)) {
                    toastr.warning(`This input only accepts values between ${addCommas(rule.min
                    )} and ${addCommas(rule.max)}.  please enter the value again.`,
                        undefined,
                        toastrCustomOptionsLonger
                    );
                } else {
                    setSectorDataInt(produce((draft) => {
                        draft[rowId][colId] = value;
                    }));
                    setSectorDataExt(produce((draft) => {
                        draft[rowId][colId] = parseFloat(value);
                    }));
                }
            }
        } else {
            setSectorDataInt(produce((draft) => {
                draft[rowId][colId] = value;
            }));
            setSectorDataExt(produce((draft) => {
                draft[rowId][colId] = value;
            }));
        }
    }, []);

    const getCellContent = ({ rowId, cell, rowData }) => {
        switch (cell.dataType) {
            case "TextField":
                return (
                    <DataEntryTextField
                        size="small"
                        variant="outlined"
                        inputProps={{ inputMode: "numeric" }}
                        style={{ width: 100, textAlign: "center" }}
                        value={(rowData && addCommas(rowData[cell.mappingProperty])) || ''}
                        onChange={(e) =>
                            handleInputChange(
                                e,
                                rowId,
                                cell.mappingProperty,
                                cell.validations
                            )}
                    />
                );
            default:
                return <></>;
        }
    };

    return (
        <>
            {sector ?
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <SectorTitleTypography>{sector.title}</SectorTitleTypography>
                    <Fragment>
                        <Table>
                            <TableHead>
                                <FormTableRow>
                                    {/* The following code segment has been commented out due to the requirement of AGC-977 */}
                                    {/* <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} ></FormTableCell>
                                    {(sector.columns || {}).map((column) => (
                                        <FormTableCell sx={{ width: CELL_WIDTH[column.cssClass] }} >
                                            {column.title}
                                        </FormTableCell>
                                    ))} */}
                                </FormTableRow>
                            </TableHead>
                            <TableBody>
                                {sector.rows.map((row) => (
                                    <FormTableRow>
                                        <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} >
                                            {row.title}
                                        </FormTableCell>
                                        {row.cells.map((cell) => (
                                            cell.visible &&
                                            <FormTableCell sx={{ width: CELL_WIDTH[cell.cssClass] ? CELL_WIDTH[cell.cssClass] : CELL_WIDTH.GeneralCell }} >
                                                {getCellContent({
                                                    rowId: row.id,
                                                    cell: cell,
                                                    rowData: sectorDataInt && sectorDataInt[row.id]
                                                })}
                                            </FormTableCell>
                                        ))}

                                    </FormTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Fragment>
                </TableContainer>
                : <Skeleton variant="rectangular" width={'100%'} height={150} />
            }
        </>
    )
};

const ElectricityFuelCont = (props) => {
    const dispatch = useDispatch();
    let navigate = useNavigate();

    const reportId = useSelector(state => state.common.reportId);
    const metaData = useSelector(state => state.electricityFuelCont.metaData);
    const initialWholeFarmData = useSelector(state => state.electricityFuelCont.initialWholeFarmData);
    const currentWholeFarmData = useSelector(state => state.electricityFuelCont.currentWholeFarmData);
    const initialFormData = useSelector(state => state.electricityFuelCont.initialFormData);
    
    const { successMsg, errorMsg } = useSelector(state => state.common);
    const { reportName, locked } = useSelector(state => state.farmReport.reportSettings);

    const [formData, setFormData] = useState({})
    const [modalOpen, setModalOpen] = useState(false);
    const [nextClicked, setNextClicked] = useState(false);
    const [wholeFarmDataInt, setWholeFarmDataInt] = useState(undefined);
    const [wholeFarmDataExt, setWholeFarmDataExt] = useState(undefined);

    // ToDo get rid of this and the duplication of initial and current data sets
     const [usedAllocated, setUsedAllocated] = useState(false);

    const sectors = metaData?.form?.sectors || [];
    const wholeFarm = metaData?.form?.wholeFarm || [];

    useEffect(() => {
        dispatch(fullResetElectricityFuelCont());
        dispatch(setLoading());
        dispatch(resetResponseState());
        dispatch(getElectricityFuelContMetaData({ reportId }));
        dispatch(getElectricityFuelContFormData({ reportId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => {
            dispatch(fullResetElectricityFuelCont());
        }
    }, []);

    useEffect(() => {
        const tempData = { ...initialFormData };
        setFormData(tempData);
        if(!_.isEmpty(initialFormData) && !_.isEmpty(initialWholeFarmData)){
            dispatch(unsetLoading());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFormData]);

    useEffect(() => {
        setWholeFarmDataInt({ ...initialWholeFarmData });
        setWholeFarmDataExt({ ...initialWholeFarmData });
        if(!_.isEmpty(initialFormData) && !_.isEmpty(initialWholeFarmData)){
            dispatch(unsetLoading());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialWholeFarmData]);

    useEffect(() => {
        if (successMsg) {
            dispatch(unsetLoading());
            toastr.success(successMsg);
            dispatch(resetResponseState());
            if (nextClicked) {
                dispatch(fullResetElectricityFuelCont());
                navigate('/data-entry/energy-waste/renewable-electricity');
            }else{
                dispatch(getElectricityFuelContFormData({ reportId }));
            }
            setNextClicked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successMsg]);

    useEffect(() => {
        if (errorMsg) {
            dispatch(unsetLoading());
            toastr.error(errorMsg, undefined, toastrCustomOptionsLonger);
            dispatch(resetResponseState());
            dispatch(getElectricityFuelContFormData({ reportId }));
            setNextClicked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorMsg]);

    const handleModalResponse = (positiveResponse) => {
        setModalOpen(false);
        if (positiveResponse) {
            // window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            dispatch(getElectricityFuelContMetaData({ reportId }));
            dispatch(getElectricityFuelContFormData({ reportId }));
        }
    }

    const isFormUpdated = () => {
        const electricityFuelContData = store.getState().electricityFuelCont;
        const isUpdatedWholeFarmData = !isEqual(electricityFuelContData?.initialWholeFarmData, electricityFuelContData?.currentWholeFarmData);
        const isUpdatedEnterpriseData = !isEqual(electricityFuelContData?.initialFormData, electricityFuelContData?.currentFormData);
        
        return (isUpdatedWholeFarmData || isUpdatedEnterpriseData || usedAllocated)
    }

    const doSave = () =>{
        dispatch(setLoading());
        dispatch(saveElectricityFuelContData({ reportId }));
        setUsedAllocated(false)
    }

    const handleSaveClick = (e) => {
        if(!isFormUpdated()){
            toastr.warning('There are no changes to save.');
            return;
        }
        doSave();
    }

    const handleNextClick = (e) => {
        setNextClicked(true);
        if (locked || !isFormUpdated()){
            //toastr.warning('There are no changes to save.');
            navigate('/data-entry/energy-waste/renewable-electricity');
        }else {
            doSave();
        };
    }

    const handleAllocateElectricityFuelClick = () => {
        if (currentWholeFarmData && reportId){
            dispatch(getAllocateElectricityFuelCont({ reportId, currentWholeFarmData }));
            setUsedAllocated(true)
        }
    }

    const handleInputChange = useCallback((e, colId, rule) => {
        let value = removeCommas(e.target.value);
        if (e.target.inputMode === "numeric") {
            let numericRegex = /^-?[0-9]*(\.[0-9]{0,2})?$/;
            if (numericRegex.test(value)) {
                if (rule.max < parseFloat(value || 0)) {
                    toastr.warning(`This input only accepts values between ${addCommas(rule.min
                    )} and ${addCommas(rule.max)}.  please enter the value again.`,
                        undefined,
                        toastrCustomOptionsLonger
                    );
                } else {
                    setWholeFarmDataInt(produce((draft) => {
                        draft[colId] = value;
                    }));
                    setWholeFarmDataExt(produce((draft) => {
                        draft[colId] = parseFloat(value);
                    }));
                }
            }
        } else {
            setWholeFarmDataInt(produce((draft) => {
                draft[colId] = value;
            }));
            setWholeFarmDataExt(produce((draft) => {
                draft[colId] = parseFloat(value);
            }));
        }
    }, []);

    useEffect(() => {
        if (wholeFarmDataExt && !_.isEmpty(wholeFarmDataExt)) {
            dispatch(setWholeFarmDataCont({
                wholeFarmData: wholeFarmDataExt
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wholeFarmDataExt]);

    return (
        <ScrollSync>
        <Grid container spacing={2} className={locked && 'deactivated'}>
            <Grid item xs={12} md={12} lg={12}>
                <FormHeaderPaper sx={{ p: 2, display: "flex", flexDirection: "column" }} divider={false}>
                    <SelectedFarm />
                    <SelectedReport reportName={reportName} />
                    <FormTitleTypography variant="h5" component="div">
                        Electricity & Fuel (cont)
                    </FormTitleTypography>
                    <FormDescriptionTypography variant="body2" paragraph>
                        For each applicable data category, enter the quantity of electricity and fuel used by the whole farm.
                        Quantities will prepopulate against each land use, crop and livestock enterprise.
                        Change the prepopulated values if required.
                    </FormDescriptionTypography>
                </FormHeaderPaper>
            </Grid>
            <Grid item xs={12} sx={{ paddingTop:'0 !important' }}>
                <Paper sx={{ p: 2, display: "flex", flexDirection: "column", paddingTop:'0' }}>
                    <List sx={{ mb: 2 }}>
                        <ListSubheader sx={{ bgcolor: "background.paper" }}>
                            <ScrollSyncPane>
                                <>
                                <DataEntryButtonStack
                                    modalOpen={modalOpen}
                                    setModalOpen={setModalOpen}
                                    handleModalResponse={handleModalResponse}
                                    handleSaveClick={handleSaveClick}
                                    handleNextClick={handleNextClick}
                                />
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <FormTableRow>
                                                <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }} ></FormTableCell>
                                                {Object.values(wholeFarm || {}).map((column) => (
                                                    <FormTableCell sx={{ width: CELL_WIDTH[column.cssClass] }} >
                                                        {column.title}
                                                    </FormTableCell>
                                                ))}
                                            </FormTableRow>
                                        </TableHead>
                                    </Table>
                                </TableContainer>
                                </>
                            </ScrollSyncPane>
                        </ListSubheader>
                        {(wholeFarmDataInt && wholeFarm) && <> <TableContainer component={Paper} sx={{ mb: 3 }}>
                            <SectorTitleTypography>Whole Farm</SectorTitleTypography>
                            <Table>
                                <TableBody>
                                    <FormTableRow>
                                        <FormTableCell sx={{ width: CELL_WIDTH['RowTitle'] }}>Whole Farm</FormTableCell>
                                        {Object.values(wholeFarm || {}).map((cell,index) => (
                                            <FormTableCell sx={{ width: CELL_WIDTH[cell.cssClass] }}>
                                                <DataEntryTextField
                                                    size="small"
                                                    variant="outlined"
                                                    inputProps={{ inputMode: "numeric" }}
                                                    style={{ width: 125, textAlign: "center" }}
                                                    value={wholeFarmDataInt && addCommas(wholeFarmDataInt[cell.mappingProperty])}
                                                    onChange={(e) => handleInputChange(e, cell.mappingProperty, cell.validations)}
                                                    autoFocus={index === 0}
                                                />
                                            </FormTableCell>
                                        ))}
                                    </FormTableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={3}>
                                <Button variant="contained" align="left" sx={{ textTransform: 'none', mb: 2 }}
                                    onClick={() => handleAllocateElectricityFuelClick()}> Allocate fuel use
                                </Button>
                            </Stack>
                        </>}
                        {formData && sectors.map((sector, index) => (
                            <>
                                <React.Fragment key={index}>
                                    <DataEntryTable
                                        key={sector.id}
                                        sector={sector}
                                        initialFormData={formData[sector.id] ? formData[sector.id] : {}}
                                    />
                                </React.Fragment>
                            </>
                        ))}
                    </List>    
                </Paper>
            </Grid>
        </Grid>
        </ScrollSync>
    )
}

export default ElectricityFuelCont